import aAnData from 'english-a-an-list/dist/aan.json';

// Type for WordPOS to avoid importing at module level
type WordPOS = any;

// Dictionary service for grammar verification and word analysis
export class DictionaryService {
  private static instance: DictionaryService;
  private wordpos: WordPOS | null = null;
  private wordposPromise: Promise<WordPOS> | null = null;
  private aAnList: Set<string>;

  private constructor() {
    this.aAnList = new Set(Object.keys(aAnData));
  }

  // Lazy load WordPOS to avoid dynamic require warnings
  private async getWordPOS(): Promise<WordPOS> {
    if (this.wordpos) {
      return this.wordpos;
    }
    
    if (this.wordposPromise) {
      return this.wordposPromise;
    }

    this.wordposPromise = import('wordpos').then((WordPOSModule) => {
      const WordPOS = WordPOSModule.default || WordPOSModule;
      this.wordpos = new WordPOS();
      return this.wordpos;
    });

    return this.wordposPromise;
  }

  public static getInstance(): DictionaryService {
    if (!DictionaryService.instance) {
      DictionaryService.instance = new DictionaryService();
    }
    return DictionaryService.instance;
  }

  /**
   * Determine if a word should use "a" or "an" as an article
   */
  public getArticle(word: string): 'a' | 'an' {
    if (!word) return 'a';
    
    const lowerWord = word.toLowerCase();
    
    // Check the a/an list first (most accurate)
    if (this.aAnList.has(lowerWord)) {
      return 'an';
    }
    
    // Fallback to vowel check
    const vowels = ['a', 'e', 'i', 'o', 'u'];
    const firstLetter = lowerWord[0];
    
    // Handle special cases
    const specialCases = ['honor', 'hour', 'herb', 'honest', 'honorable', 'heir', 'heiress'];
    if (specialCases.some(specialCase => lowerWord.startsWith(specialCase))) {
      return 'an';
    }
    
    return vowels.includes(firstLetter) ? 'an' : 'a';
  }

  /**
   * Get part of speech for a word
   */
  public async getPartOfSpeech(word: string): Promise<string[]> {
    try {
      const wordpos = await this.getWordPOS();
      const pos = await wordpos.getPOS(word);
      const parts: string[] = [];
      
      if (pos.nouns && pos.nouns.length > 0) parts.push('noun');
      if (pos.verbs && pos.verbs.length > 0) parts.push('verb');
      if (pos.adjectives && pos.adjectives.length > 0) parts.push('adjective');
      if (pos.adverbs && pos.adverbs.length > 0) parts.push('adverb');
      
      return parts;
    } catch (error) {
      console.warn('Error getting part of speech for word:', word, error);
      return [];
    }
  }

  /**
   * Check if a word is a valid English word
   */
  public async isValidWord(word: string): Promise<boolean> {
    try {
      const wordpos = await this.getWordPOS();
      const pos = await wordpos.getPOS(word);
      return !!(pos.nouns?.length || pos.verbs?.length || pos.adjectives?.length || pos.adverbs?.length);
    } catch (error) {
      return false;
    }
  }

  /**
   * Get synonyms for a word
   */
  public async getSynonyms(word: string, partOfSpeech?: string): Promise<string[]> {
    try {
      const wordpos = await this.getWordPOS();
      let synonyms: string[] = [];
      
      if (partOfSpeech === 'noun' || !partOfSpeech) {
        const nounSynonyms = await wordpos.lookupNoun(word);
        synonyms = synonyms.concat(nounSynonyms.flatMap((synset: any) => synset.lemma));
      }
      
      if (partOfSpeech === 'verb' || !partOfSpeech) {
        const verbSynonyms = await wordpos.lookupVerb(word);
        synonyms = synonyms.concat(verbSynonyms.flatMap((synset: any) => synset.lemma));
      }
      
      if (partOfSpeech === 'adjective' || !partOfSpeech) {
        const adjSynonyms = await wordpos.lookupAdjective(word);
        synonyms = synonyms.concat(adjSynonyms.flatMap((synset: any) => synset.lemma));
      }
      
      if (partOfSpeech === 'adverb' || !partOfSpeech) {
        const advSynonyms = await wordpos.lookupAdverb(word);
        synonyms = synonyms.concat(advSynonyms.flatMap((synset: any) => synset.lemma));
      }
      
      // Remove duplicates and the original word
      const uniqueSynonyms = Array.from(new Set(synonyms));
      return uniqueSynonyms.filter(syn => syn !== word);
    } catch (error) {
      console.warn('Error getting synonyms for word:', word, error);
      return [];
    }
  }

  /**
   * Get antonyms for a word
   */
  public async getAntonyms(word: string, partOfSpeech?: string): Promise<string[]> {
    try {
      const wordpos = await this.getWordPOS();
      let antonyms: string[] = [];
      
      if (partOfSpeech === 'noun' || !partOfSpeech) {
        const nounAntonyms = await wordpos.lookupNoun(word);
        antonyms = antonyms.concat(nounAntonyms.flatMap((synset: any) => 
          synset.pointers?.filter((p: any) => p.pointerSymbol === '!')?.map((p: any) => p.targetLemma) || []
        ));
      }
      
      if (partOfSpeech === 'verb' || !partOfSpeech) {
        const verbAntonyms = await wordpos.lookupVerb(word);
        antonyms = antonyms.concat(verbAntonyms.flatMap((synset: any) => 
          synset.pointers?.filter((p: any) => p.pointerSymbol === '!')?.map((p: any) => p.targetLemma) || []
        ));
      }
      
      if (partOfSpeech === 'adjective' || !partOfSpeech) {
        const adjAntonyms = await wordpos.lookupAdjective(word);
        antonyms = antonyms.concat(adjAntonyms.flatMap((synset: any) => 
          synset.pointers?.filter((p: any) => p.pointerSymbol === '!')?.map((p: any) => p.targetLemma) || []
        ));
      }
      
      // Remove duplicates and the original word
      const uniqueAntonyms = Array.from(new Set(antonyms));
      return uniqueAntonyms.filter(ant => ant !== word);
    } catch (error) {
      console.warn('Error getting antonyms for word:', word, error);
      return [];
    }
  }

  /**
   * Validate grammar in a sentence or phrase
   */
  public async validateGrammar(text: string): Promise<{
    isValid: boolean;
    issues: string[];
    suggestions: string[];
  }> {
    const issues: string[] = [];
    const suggestions: string[] = [];
    
    try {
      // Check for common grammar issues
      const words = text.toLowerCase().match(/\b\w+\b/g) || [];
      
      // Check for article usage
      for (let i = 0; i < words.length - 1; i++) {
        if (words[i] === 'a' || words[i] === 'an') {
          const nextWord = words[i + 1];
          const correctArticle = this.getArticle(nextWord);
          if (words[i] !== correctArticle) {
            issues.push(`Incorrect article: "${words[i]} ${nextWord}" should be "${correctArticle} ${nextWord}"`);
            suggestions.push(`Use "${correctArticle}" instead of "${words[i]}"`);
          }
        }
      }
      
      // Check for awkward phrasing patterns
      const awkwardPatterns = [
        {
          pattern: /(\w+) and (\w+)!/gi,
          check: (adj: string, color: string) => {
            const colors = ['red', 'blue', 'green', 'yellow', 'purple', 'orange', 'pink', 'brown', 'black', 'white', 'silver', 'gold', 'gray', 'grey'];
            return colors.includes(color.toLowerCase());
          },
          message: (match: string, adj: string, color: string) => 
            `Awkward phrasing: "${match}" - Consider "${adj} ${color} [noun]" instead`
        }
      ];
      
      awkwardPatterns.forEach(pattern => {
        let match;
        while ((match = pattern.pattern.exec(text)) !== null) {
          const fullMatch = match[0];
          const adj = match[1];
          const color = match[2];
          if (pattern.check(adj, color)) {
            issues.push(pattern.message(fullMatch, adj, color));
          }
        }
        pattern.pattern.lastIndex = 0; // Reset regex
      });
      
      return {
        isValid: issues.length === 0,
        issues,
        suggestions
      };
    } catch (error) {
      console.warn('Error validating grammar:', error);
      return {
        isValid: true,
        issues: [],
        suggestions: []
      };
    }
  }

  /**
   * Get word suggestions for better grammar
   */
  public async getWordSuggestions(word: string, context: string): Promise<{
    synonyms: string[];
    antonyms: string[];
    partOfSpeech: string[];
    suggestions: string[];
  }> {
    try {
      const [synonyms, antonyms, partOfSpeech] = await Promise.all([
        this.getSynonyms(word),
        this.getAntonyms(word),
        this.getPartOfSpeech(word)
      ]);
      
      const suggestions: string[] = [];
      
      // Add synonyms as suggestions
      if (synonyms.length > 0) {
        suggestions.push(`Synonyms: ${synonyms.slice(0, 3).join(', ')}`);
      }
      
      // Add part of speech info
      if (partOfSpeech.length > 0) {
        suggestions.push(`Part of speech: ${partOfSpeech.join(', ')}`);
      }
      
      return {
        synonyms,
        antonyms,
        partOfSpeech,
        suggestions
      };
    } catch (error) {
      console.warn('Error getting word suggestions:', error);
      return {
        synonyms: [],
        antonyms: [],
        partOfSpeech: [],
        suggestions: []
      };
    }
  }
}

// Export singleton instance
export const dictionaryService = DictionaryService.getInstance();
